import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { cachedGet } from "@/lib/api";
import { LayoutDashboard, FileStack, AlertTriangle, MessageSquareWarning, Scale, LogOut, Moon, Sun, PiggyBank, Menu, X, CalendarClock, Radar, MessageSquareHeart } from "lucide-react";
import OverviewView from "@/pages/views/OverviewView";
import ContractsView from "@/pages/views/ContractsView";
import ReportView from "@/pages/views/ReportView";
import PepinView from "@/pages/views/PepinView";
import SimulatorView from "@/pages/views/SimulatorView";
import RemindersView from "@/pages/views/RemindersView";
import DiscoveryView from "@/pages/views/DiscoveryView";
import FeedbackAdminView from "@/pages/views/FeedbackAdminView";
import { HelpGuide } from "@/components/HelpGuide";
import CardPicker from "@/components/CardPicker";
import PriceModal from "@/components/PriceModal";
import FeedbackWidget from "@/components/FeedbackWidget";
import InviteButton from "@/components/InviteButton";

const TABS = [
  { id: "overview", label: "Tableau de bord", icon: LayoutDashboard },
  { id: "discovery", label: "Recherche auto", icon: Radar },
  { id: "contracts", label: "Mes Contrats", icon: FileStack },
  { id: "report", label: "Doublons & Failles", icon: AlertTriangle },
  { id: "pepin", label: "Assistant Pépin", icon: MessageSquareWarning },
  { id: "simulator", label: "Dois-je la prendre ?", icon: Scale },
  { id: "reminders", label: "Échéances", icon: CalendarClock },
];

export default function Shell() {
  const { user, logout } = useAuth();
  const navTabs = [...TABS, ...(user?.role === "admin" ? [{ id: "feedback", label: "Avis testeurs", icon: MessageSquareHeart }] : [])];
  const [tab, setTab] = useState("overview");
  const [savings, setSavings] = useState(null);
  const [remindersCount, setRemindersCount] = useState(0);
  const [dark, setDark] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(user?.onboarding_completed === false);
  const [showCards, setShowCards] = useState(false);
  const [priceQueue, setPriceQueue] = useState([]);
  const [priceTotal, setPriceTotal] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  const finishOnboarding = async () => {
    setShowOnboarding(false);
    try {
      await api.post("/auth/onboarding-complete");
    } catch {
      /* best-effort */
    }
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const loadSavings = async () => {
    try {
      const { data } = await cachedGet("/analysis/report");
      setSavings(data.annual_savings || 0);
    } catch {
      setSavings(null);
    }
    try {
      const { data } = await api.get("/reminders");
      setRemindersCount((data || []).filter((r) => typeof r.days_left === "number" && r.days_left <= 60).length);
    } catch {
      setRemindersCount(0);
    }
  };

  useEffect(() => {
    loadSavings();
  }, [refreshKey]);

  const onContractsChanged = () => setRefreshKey((k) => k + 1);

  const promptPricesFor = (list) => {
    const missing = (list || []).filter((c) => c && c.monthly_cost == null && !c.needs_import);
    if (missing.length) {
      setPriceQueue(missing);
      setPriceTotal(missing.length);
    }
  };

  const onPriceDone = () => {
    setPriceQueue((q) => q.slice(1));
    onContractsChanged();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/85 border-b border-border safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="IACover" className="h-7 w-auto dark:hidden" data-testid="app-logo" />
            <img src="/logo-mark-light.png" alt="IACover" className="h-7 w-auto hidden dark:block" />
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {savings > 0 && (
              <div data-testid="savings-counter" className="hidden sm:flex items-center gap-2 pl-3 pr-3.5 py-1.5 rounded-full bg-accent text-accent-foreground border border-ochre/30">
                <PiggyBank className="w-4 h-4" />
                <span className="text-sm font-semibold font-mono">{Math.round(savings)} €/an économisables</span>
              </div>
            )}
            <InviteButton />
            <button data-testid="theme-toggle" onClick={() => setDark((d) => !d)} className="w-9 h-9 grid place-items-center rounded-xl border border-border hover:bg-muted transition">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <HelpGuide variant="icon" />
            <div className="hidden md:flex items-center gap-2 pl-2">
              {user?.picture && !avatarError ? (
                <img
                  src={user.picture}
                  alt={user?.name || "Profil"}
                  referrerPolicy="no-referrer"
                  onError={() => setAvatarError(true)}
                  data-testid="user-avatar"
                  className="w-8 h-8 rounded-full object-cover border border-border"
                />
              ) : (
                <div data-testid="user-avatar-initial" className="w-8 h-8 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-semibold">
                  {(user?.name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <button data-testid="logout-btn" onClick={logout} className="text-sm text-muted-foreground hover:text-destructive transition flex items-center gap-1.5">
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </div>
            <button data-testid="mobile-menu-btn" className="md:hidden w-9 h-9 grid place-items-center rounded-xl border border-border" onClick={() => setMobileNav((v) => !v)}>
              {mobileNav ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Desktop tabs */}
        <nav className="hidden md:block border-t border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
            {navTabs.map((t) => (
              <button
                key={t.id}
                data-testid={`nav-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3.5 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                  tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
                {t.id === "reminders" && remindersCount > 0 && (
                  <span data-testid="reminders-badge" className="ml-1 min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">{remindersCount}</span>
                )}
              </button>
            ))}
          </div>
        </nav>

        {/* Mobile nav */}
        {mobileNav && (
          <nav className="md:hidden border-t border-border bg-background p-2 space-y-1">
            {navTabs.map((t) => (
              <button
                key={t.id}
                data-testid={`nav-mobile-${t.id}`}
                onClick={() => { setTab(t.id); setMobileNav(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
                {t.id === "reminders" && remindersCount > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">{remindersCount}</span>
                )}
              </button>
            ))}
            <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-destructive">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </nav>
        )}
      </header>

      {!online && (
        <div data-testid="offline-banner" className="bg-ochre/15 text-ochre text-center text-xs font-medium py-1.5 px-4 border-b border-ochre/30">
          Mode hors-ligne — vous consultez vos dernières données enregistrées
        </div>
      )}

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {tab === "overview" && <OverviewView key={refreshKey} onGoto={setTab} />}
        {tab === "discovery" && <DiscoveryView onChanged={onContractsChanged} />}
        {tab === "contracts" && <ContractsView onChanged={onContractsChanged} />}
        {tab === "report" && <ReportView key={refreshKey} />}
        {tab === "pepin" && <PepinView />}
        {tab === "simulator" && <SimulatorView />}
        {tab === "reminders" && <RemindersView />}
        {tab === "feedback" && user?.role === "admin" && <FeedbackAdminView />}
      </main>

      <HelpGuide onboarding controlledOpen={showOnboarding} onClose={finishOnboarding} onAddCards={() => setShowCards(true)} />
      <CardPicker open={showCards} onClose={() => setShowCards(false)} onAdded={(added) => { onContractsChanged(); promptPricesFor(added); }} />
      <PriceModal
        open={priceQueue.length > 0}
        contract={priceQueue[0]}
        index={priceTotal - priceQueue.length + 1}
        total={priceTotal}
        onSaved={onPriceDone}
        onClose={() => setPriceQueue((q) => q.slice(1))}
      />
      <FeedbackWidget />
    </div>
  );
}
